import { useCallback, useEffect } from 'react';

import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent, EVENT_TYPES, RUDDERSTACK_EVENTS } from '@/feature/analytics/constants';
import { trackRudderStackEvent } from '@/feature/analytics/trackRudderStackEvent';
import { userLimitsKeys } from '@/feature/responsible-gaming/hooks/query-keys';
import { withdrawalMethodKeys } from '@/feature/withdraw/hooks/use-withdrawal-methods';
import {
    authUserKeys,
    hasMadeFirstDepositSelector,
    useAuthUserConfig,
    userCreatedAtTimestampSelector,
} from '@/hooks/use-auth-user-config';
import { getUserInfo } from '@/hooks/use-auth-user-info';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { logger } from '@/utils/logging';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInDays } from 'date-fns';

import { PayPalStatus, constructFTDPayload, getDepositStatus, getFormattedDepositAmount } from '../utils';
import { PaymentMethodType } from './use-deposit-payment-methods';

const DEPOSIT_STATUS_LOG_TAG = '[Deposit-Status]';

// this event name is currently required to sync with marketing tracking
const FIRST_DEPOSIT_COMPLETE = 'first_deposit_complete';
const FIRST_DEPOSIT_FAILURE = 'first_deposit_failure';

export const useTrackFirstDepositEvent = (
    currentStatus: PayPalStatus,
    paymentProvider: PaymentMethodType,
    selectedAmount: string,
    errorCode?: string
) => {
    const queryClient = useQueryClient();

    const { data: hasMadeFirstDeposit, isLoading } = useAuthUserConfig({
        select: hasMadeFirstDepositSelector,
    });

    const jurisdiction = useJurisdictionStore(state => state.jurisdiction);
    const product = useJurisdictionStore(state => state.product);

    const { data: userCreatedAtTimestamp } = useAuthUserConfig({
        select: userCreatedAtTimestampSelector,
    });

    // Calculate the number of days between user creation and now.
    // Using +1 to include the registration day as the first day, tomorrow 2nd day, etc.
    const userRegisterToFtdDays =
        userCreatedAtTimestamp !== undefined
            ? differenceInDays(new Date(), new Date(userCreatedAtTimestamp)) + 1
            : undefined;

    const trackFirstDepositEvent = useCallback(async () => {
        const depositAmount = getFormattedDepositAmount(selectedAmount);
        const ftdPayload = constructFTDPayload(paymentProvider, currentStatus, depositAmount);
        const depositStatus = getDepositStatus(currentStatus);
        let userInfo = null;
        try {
            userInfo = await getUserInfo();
        } catch (e) {
            logger.error(DEPOSIT_STATUS_LOG_TAG, 'Error loading user info', e);
        }

        BetrAnalytics.trackProductEvent(AnalyticsEvent.FTD_ATTEMPT_SUCCESS);

        const additionalFields: Record<string, string | number> = {};

        if (currentStatus === PayPalStatus.SUCCESS) {
            Object.assign(additionalFields, {
                ...(jurisdiction && { state: jurisdiction }),
                ...(product && { vertical: product }),
                ...(userRegisterToFtdDays !== undefined && { registerToFtdDays: userRegisterToFtdDays }),
            });
        }

        trackRudderStackEvent(
            RUDDERSTACK_EVENTS.custom(
                currentStatus === PayPalStatus.SUCCESS ? FIRST_DEPOSIT_COMPLETE : FIRST_DEPOSIT_FAILURE
            ),
            {
                ...ftdPayload,
                eventType: EVENT_TYPES.FTD,
                depositStatus,
                ...(userInfo?.data ?? {}),
                ...(errorCode ? { errorCode } : {}),
                ...additionalFields,
            }
        );
    }, [currentStatus, errorCode, paymentProvider, selectedAmount, jurisdiction, product, userRegisterToFtdDays]);

    useEffect(() => {
        if (!hasMadeFirstDeposit && !isLoading) {
            trackFirstDepositEvent();
        }
    }, [hasMadeFirstDeposit, isLoading, trackFirstDepositEvent]);

    useEffect(() => {
        if (currentStatus === PayPalStatus.SUCCESS) {
            queryClient.invalidateQueries({ queryKey: userLimitsKeys.limits() });
            queryClient.invalidateQueries({ queryKey: withdrawalMethodKeys.all });
            queryClient.invalidateQueries({ queryKey: authUserKeys.config() });
        }
    }, [currentStatus, queryClient, paymentProvider, selectedAmount, hasMadeFirstDeposit]);
};
